import cx from "classnames";
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useIntl } from "react-intl";
import { makeStyles } from "@material-ui/core/styles";
import { Box, createStyles, Theme, Typography } from "@material-ui/core";
import CustomIcon from "../../core/custom-icon";
import { Fragment } from "react";
import { lookup as mimeLookup } from "mime-types";

interface Props {
  onUpload: (file: any) => void;
  maxUpload: number;
  supportedTypes: string[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      width: "100%",
      height: "100%",
      border: 0,
      padding: theme.spacing(2),
      margin: 0,
      display: "flex",
      alignItems: "flex-end",
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.common.white,
      textAlign: "left",
      outline: 0,
      "&:focus": {
        outline: 0,
      },
    },
    dropArea: {
      border: `2px dashed ${theme.palette.primary.main}`,
      borderLeft: 0,
      padding: theme.spacing(2),
    },
  })
);

export const MeetingLobbyFileUpload = ({
  onUpload,
  maxUpload,
  supportedTypes,
}: Props) => {
  const { formatMessage } = useIntl();
  const onDrop = useCallback(
    (acceptedFiles) => {
      onUpload(acceptedFiles[0]);
    },
    [onUpload]
  );
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: supportedTypes
      .map((t) => mimeLookup(t))
      .filter((t) => !!t) as string[]
  });
  const classes = useStyles();
  return (
    <div
      {...getRootProps()}
      className={cx("fileupload", {
        "drag-active": isDragActive,
      })}
    >
      <input {...getInputProps()} />
      <Box display="flex">
        <Box flex="1 1 50%" height="143px">
          <button onClick={() => open()} className={classes.button}>
            <Box>
              <Box
                display="flex"
                alignItems="center"
                component="p"
                fontSize="body1.fontSize"
              >
                <Box component="strong" mr={1}>
                  {formatMessage({
                    id: "MeetingLobbyUploadButton",
                    defaultMessage: "Datei Auswählen",
                  })}
                </Box>
                <CustomIcon iconname="arrowRight" fontSize="default" />
              </Box>
              <Typography variant="body2">
                {supportedTypes.join(", ")}
                <br />
                {`(max. ${maxUpload} MB)`}
              </Typography>
            </Box>
          </button>
        </Box>
        <Box flex="1 1 50%" color="primary.main" className={classes.dropArea}>
          {isDragActive ? (
            <Fragment>
              <Typography component="p" variant="h3">
                {formatMessage({
                  id: "MeetingLobbyUploadDragActive",
                  defaultMessage: "Datei hier ablegen.",
                })}
              </Typography>
              <Box textAlign="center">
                <CustomIcon
                  iconname="dragAndDropDocument"
                  viewBox="0 0 40 40 "
                />
              </Box>
            </Fragment>
          ) : (
            <Fragment>
              <Typography component="p" variant="h3">
                {formatMessage({
                  id: "MeetingLobbyUploadDragNotice",
                  defaultMessage: "Oder Datei hier reinziehen.",
                })}
              </Typography>
              <Box textAlign="center" mt={2.5}>
                <CustomIcon
                  iconname="dragAndDropDocument"
                  viewBox="0 0 40 40"
                  fontSize="large"
                />
              </Box>
            </Fragment>
          )}
        </Box>
      </Box>
    </div>
  );
};
